import axios from 'axios';

// VERSIÓN: 4.0 - Detección de entorno ultra-defensiva (producción por defecto)
// Determinar el baseURL según el entorno
const getBaseURL = () => {
  // Si hay una variable de entorno, usarla (tiene prioridad)
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  
  // Verificar que window y location estén disponibles
  if (typeof window === 'undefined' || !window.location || !window.location.hostname) {
    // Fallback seguro: asumir producción (ruta relativa)
    return '/api';
  }
  
  // Detectar si estamos en desarrollo SOLO por hostname (más confiable)
  // NO usar process.env.NODE_ENV porque puede estar hardcodeado en el build
  const hostname = window.location.hostname.toLowerCase();
  const protocol = window.location.protocol;
  
  // FORZAR producción si es HTTPS (nunca usar localhost en HTTPS público)
  if (protocol === 'https:') {
    return '/api';
  }
  
  // Lista MUY RESTRICTIVA de hostnames de desarrollo
  // Solo estos hostnames específicos usarán localhost
  const developmentHostnames = [
    'localhost',
    '127.0.0.1',
    '0.0.0.0'
  ];
  
  // Verificar si es un hostname de desarrollo explícito
  const isDevelopmentHostname = developmentHostnames.includes(hostname);
  
  // Verificar si es una IP privada (red local) SOLO si es HTTP (no HTTPS)
  // Esto evita que IPs privadas en producción usen localhost
  const isPrivateIP = protocol === 'http:' && (
    hostname.startsWith('192.168.') ||
    hostname.startsWith('10.') ||
    (hostname.startsWith('172.') && 
     parseInt(hostname.split('.')[1] || 0) >= 16 && 
     parseInt(hostname.split('.')[1] || 0) <= 31)
  );
  
  // SOLO usar localhost si es explícitamente desarrollo
  // CUALQUIER otro caso (incluyendo hostnames desconocidos) usa producción
  if (isDevelopmentHostname || isPrivateIP) {
    return 'http://localhost:5002/api';
  }
  
  // POR DEFECTO: producción (ruta relativa)
  // Nginx hace proxy_pass de /api a localhost:5002 (puerto del backend diocesis)
  // Incluye: diocesisdeipiales.org, cualquier dominio, IPs públicas, etc.
  return '/api';
};

const api = axios.create({
  baseURL: getBaseURL(),
  headers: {
    'Content-Type': 'application/json'
  },
  timeout: 15000 // 15 segundos (evita fallos en conexiones lentas)
});

// Interceptor para agregar token a las peticiones Y corregir baseURL dinámicamente
api.interceptors.request.use(
  (config) => {
    // RECALCULAR baseURL en cada petición para evitar problemas de caché
    const correctBaseURL = getBaseURL();
    if (config.baseURL !== correctBaseURL) {
      config.baseURL = correctBaseURL;
    }
    
    // Si la URL completa contiene localhost:5001 y estamos en producción, corregirla
    if (config.url && typeof window !== 'undefined' && window.location) {
      const isProduction = window.location.protocol === 'https:' || 
                          (window.location.hostname !== 'localhost' && 
                           window.location.hostname !== '127.0.0.1' &&
                           window.location.hostname !== '0.0.0.0');
      
      if (isProduction && (config.url.includes('localhost:500') || config.baseURL?.includes('localhost:500'))) {
        // Forzar ruta relativa en producción
        config.baseURL = '/api';
        config.url = config.url.replace(/https?:\/\/localhost:500\d\/api\//g, '');
        config.url = config.url.replace(/https?:\/\/localhost:500\d/g, '');
      }
    }
    
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // No establecer Content-Type si es FormData (el navegador lo hará automáticamente)
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Reintento breve si el servidor aplicó límite de peticiones (429)
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config;
    const status = error.response?.status;
    if (status === 429 && config && !config.__retry429) {
      config.__retry429 = true;
      await new Promise((r) => setTimeout(r, 2000));
      return api.request(config);
    }

    // Solo redirigir a login si es un error 401 Y estamos en una ruta de admin
    if (status === 401) {
      const currentPath = window.location.pathname;
      const isAdminRoute = currentPath.includes('/admin') && !currentPath.includes('/admin/login');
      
      // Limpiar tokens
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      
      // Solo redirigir si estamos en una ruta de admin protegida
      if (isAdminRoute) {
        window.location.href = '/admin/login';
      }
    }
    return Promise.reject(error);
  }
);

export default api;


